/* eslint-disable no-param-reassign */
/* eslint-disable no-case-declarations */
/* eslint-disable react/no-array-index-key */
import {isArray, isNull, isString, isUndefined} from 'lodash';
import mdast from 'mdast';
import React, {Children, cloneElement, Fragment, isValidElement, ReactElement, ReactNode} from 'react';

import {
  isMarkdownDecoratedCountChildrenTextContentComponent,
  isMarkdownDecoratedSkipCountingTextContentComponent,
} from './markdownDecoratedComponents';
import {NodeTransform} from './markdownFormatterTypes';
import {
  AnnotationComponent,
  AnnotationLike,
  renderStringWithAnnotations,
} from './renderStringWithAnnotations';

export type MarkdownAstToReactComponentsMap<T extends AnnotationLike> = Readonly<{
  ErrorFallbackComponent: React.ComponentType<{error: unknown; debugIdentifier: string}>;
  LineBreak: ReactElement;
  Paragraph: React.ComponentType<{children: React.ReactNode; isFirstTextNode: boolean}>;
  Blockquote: React.ComponentType<{children: React.ReactNode}>;
  UnorderedList: React.ComponentType<{
    children: React.ReactNode;
    listStyle: UnorderedListBulletTypesEnum;
    depth: number;
    isFirstTextNode: boolean;
  }>;
  OrderedList: React.ComponentType<{
    children: React.ReactNode;
    listStyle: OrderedListBulletTypesEnum;
    start: number | undefined;
    depth: number;
    isFirstTextNode: boolean;
  }>;
  ListItem: React.ComponentType<{children: React.ReactNode; index: number}>;
  Code: React.ComponentType<{children: React.ReactNode; isFirstTextNode: boolean}>;
  InlineCode: React.ComponentType<{children: React.ReactNode}>;
  Emphasis: React.ComponentType<{children: React.ReactNode}>;
  Strong: React.ComponentType<{children: React.ReactNode}>;
  Delete: React.ComponentType<{children: React.ReactNode}>;
  Link: React.ComponentType<{
    children: React.ReactNode;
    href: string;
    title?: string;
  }>;
  Annotation: AnnotationComponent<T>;
  SkipTextContentIndicesComponentWrapper: React.ComponentType<{children: React.ReactNode}>;
}>;

/**
 * Renders a markdown AST to React components.
 * @param root The Markdown AST Root.
 * @param annotations All the annotations that should be rendered on the text.
 * @param componentTransform Map of all the components that need to be provided to properly render. One implementation for web and one for mobile.
 * It also contains a textTransform function that may transform each strings into more complex React nodes (mentions, inline unfurls, emojis...)
 */
export function renderMarkdownAst<T extends AnnotationLike>(
  root: mdast.Root,
  annotations: ReadonlyArray<T>,
  componentTransform: MarkdownAstToReactComponentsMap<T> & {textTransform?: NodeTransform},
): ReactNode {
  const {
    LineBreak,
    Paragraph,
    Blockquote,
    OrderedList,
    UnorderedList,
    ListItem,
    SkipTextContentIndicesComponentWrapper,
  } = componentTransform;
  const {Code, InlineCode, Emphasis, Strong, Delete, Link, Annotation, textTransform} = componentTransform;

  const textContentIndexState = {index: 0};
  let previousTextToken: string | undefined;

  const maybeApplyTransformText: NodeTransform = (node, previousToken) => {
    if (!textTransform) {
      return node;
    }
    return textTransform(node, previousToken);
  };

  const renderChildren = (children: mdast.RootContent[], listDepth: number, shouldTrim: boolean = true) =>
    children.map((child, index) => (
      <Fragment key={index}>
        {render(child, listDepth, shouldTrim && index === 0, shouldTrim && index === children.length - 1)}
      </Fragment>
    ));

  const renderBlock = (children: mdast.RootContent[], listDepth: number) => {
    if (children.length === 0) {
      return LineBreak;
    }
    return renderChildren(children, listDepth);
  };

  // We need to reset the previous text token when we start rendering a new block
  // and we should not reset it when we render a node inline.
  // This is used to:
  // - determine whether or not we show an emoji or just text depending on
  // what came before it.
  // - determine if there should be a small or big top margin on mobile
  // (we want a smaller one when we're at the top).
  const resetPreviousTextToken = () => {
    previousTextToken = '';
  };

  const renderWithAnnotations = (node: ReactNode) =>
    maybeInsertAnnotations(
      node,
      textContentIndexState,
      SkipTextContentIndicesComponentWrapper,
      Annotation,
      annotations,
    );

  const render = (
    node: mdast.Root | mdast.RootContent,
    listDepth: number,
    isFirst: boolean,
    isLast: boolean,
  ): ReactNode => {
    const isFirstTextNode = isUndefined(previousTextToken);

    switch (node.type) {
      case 'root': {
        return renderBlock(node.children, listDepth);
      }

      case 'paragraph': {
        resetPreviousTextToken();
        return (
          <Paragraph isFirstTextNode={isFirstTextNode}>{renderChildren(node.children, listDepth)}</Paragraph>
        );
      }

      case 'blockquote': {
        resetPreviousTextToken();
        return <Blockquote>{renderBlock(node.children, listDepth)}</Blockquote>;
      }

      case 'list': {
        // We don't reset the previous text token here because list items are properly wrapped in paragraphs
        const renderListRoot = (children: React.ReactNode) => {
          // node.start can be null, contrary to the mdast typings.
          const start =
            !isUndefined(node.start) && !isNull(node.start) && node.start !== 1 ? node.start : undefined;

          if (node.ordered) {
            return (
              <OrderedList
                start={start}
                listStyle={findOrderedListStyleType(listDepth)}
                depth={listDepth}
                isFirstTextNode={isFirstTextNode}
              >
                {children}
              </OrderedList>
            );
          }
          return (
            <UnorderedList
              listStyle={findUnorderedListStyleType(listDepth)}
              depth={listDepth}
              isFirstTextNode={isFirstTextNode}
            >
              {children}
            </UnorderedList>
          );
        };

        // Only used on mobile, as web automatically computes list numbers
        let listIndex = 1;

        return renderListRoot(
          node.children.map((child, index) => {
            const key = `${listDepth}-${index}`;
            // Case 1 - the only content in the list item is the list, so we don't render the list item at all.
            if (child.children.length === 1 && child.children[0].type === 'list') {
              return <Fragment key={key}>{render(child.children[0], listDepth + 1, true, true)}</Fragment>;
            }
            // Case 2 - There is content in the list item before the nested list. So we first need to render
            // that content, and then the list.
            return (
              <ListItem key={key} index={listIndex++}>
                {renderBlock(child.children, listDepth + 1)}
              </ListItem>
            );
          }),
        );
      }

      case 'html':
        return null;

      case 'code': {
        previousTextToken = node.value;
        return <Code isFirstTextNode={isFirstTextNode}>{renderWithAnnotations(node.value)}</Code>;
      }

      case 'text':
        // We trim for backward compatibility with markdown-it...
        const trimmedText = maybeTrimText(node.value, isFirst, isLast);
        const transformedNode = maybeApplyTransformText(trimmedText, previousTextToken);
        // We should not pass the trimmed value here
        previousTextToken = node.value;
        return renderWithAnnotations(transformedNode);

      case 'emphasis': {
        return <Emphasis>{renderChildren(node.children, listDepth)}</Emphasis>;
      }

      case 'strong': {
        return <Strong>{renderChildren(node.children, listDepth)}</Strong>;
      }

      case 'delete': {
        return <Delete>{renderChildren(node.children, listDepth)}</Delete>;
      }

      case 'inlineCode': {
        // This is for backward compatibility with markdown-it, for some reason emojis
        // are transformed in inline code blocks but not in code blocks.
        const transformed = maybeApplyTransformText(node.value, previousTextToken);
        previousTextToken = node.value;
        return <InlineCode>{renderWithAnnotations(transformed)}</InlineCode>;
      }

      case 'break':
        resetPreviousTextToken();
        return LineBreak;

      case 'link': {
        // For backward compatibility: we don't trim the children of links.
        return (
          <Link href={node.url} title={node.title ?? undefined}>
            {renderChildren(node.children, listDepth, false)}
          </Link>
        );
      }

      case 'image': {
        // For security reasons, we don't allow images.
        // svgs can be used for malicious purposes.
        return null;
      }

      /*
       * Backward compatibility section.
       */

      case 'heading': {
        // Very basic support to try to maintain content indices.
        // We do +1 for the space. Does not work with the "Alt Heading" syntax.
        textContentIndexState.index += node.depth + 1;
        resetPreviousTextToken();
        return (
          <Paragraph isFirstTextNode={isFirstTextNode}>{renderChildren(node.children, listDepth)}</Paragraph>
        );
      }

      // Legacy support, impossible to write this in the composer anymore
      case 'linkReference': {
        resetPreviousTextToken();
        textContentIndexState.index += 1;
        const rendered = <>[{renderChildren(node.children, listDepth)}]</>;
        textContentIndexState.index += 1;
        switch (node.referenceType) {
          case 'full':
            const title = node.label ? `[${node.label}]` : '';
            return (
              <Paragraph isFirstTextNode={isFirstTextNode}>
                {rendered}
                {renderWithAnnotations(title)}
              </Paragraph>
            );
          case 'shortcut':
          case 'collapsed':
          default:
            return rendered;
        }
      }
      // Legacy support, not possible to write this in the composer anymore
      case 'definition': {
        resetPreviousTextToken();
        const label = node.label ? `[${node.label}]: ` : '';
        const title = node.title ? ` "${node.title}"` : '';
        return (
          <Paragraph isFirstTextNode={isFirstTextNode}>
            {renderWithAnnotations(label)}
            <Link href={node.url} title={node.title ?? undefined}>
              {renderWithAnnotations(node.url)}
            </Link>
            {renderWithAnnotations(title)}
          </Paragraph>
        );
      }
      // Legacy support, not possible to write this in the composer anymore
      case 'imageReference': {
        resetPreviousTextToken();
        const alt = node.alt ? `![${node.alt}]: ` : '';
        const label = node.label ? `[${node.label}]` : '';
        const text = `${alt}${label}`;
        return <Paragraph isFirstTextNode={isFirstTextNode}>{renderWithAnnotations(text)}</Paragraph>;
      }

      case 'table':
        resetPreviousTextToken();
        // There is no way to actually support text content indices for annotations in tables
        // as we don't know the number of hyphens used. The content of the table is renderer inline
        // without any notion of column or row. At least you have access to the text content.
        return (
          <Paragraph isFirstTextNode={isFirstTextNode}>{renderChildren(node.children, listDepth)}</Paragraph>
        );
      case 'tableRow':
      case 'tableCell':
        resetPreviousTextToken();
        // Very basic support.
        return <>{renderChildren(node.children, listDepth)} </>;

      default:
        return null;
    }
  };

  return render(root, 0, true, true);
}

export enum OrderedListBulletTypesEnum {
  DECIMAL = 'DECIMAL',
  LOWER_ALPHA = 'LOWER_ALPHA',
  LOWER_ROMAN = 'LOWER_ROMAN',
}

export enum UnorderedListBulletTypesEnum {
  DISC = 'DISC',
  CIRCLE = 'CIRCLE',
  SQUARE = 'SQUARE',
}

function findOrderedListStyleType(listDepth: number): OrderedListBulletTypesEnum {
  if (listDepth < 0) {
    throw new Error('List depth cannot be less than 0.');
  }

  const depth = listDepth % 3;

  switch (depth) {
    case 0:
      return OrderedListBulletTypesEnum.DECIMAL;
    case 1:
      return OrderedListBulletTypesEnum.LOWER_ALPHA;
    case 2:
      return OrderedListBulletTypesEnum.LOWER_ROMAN;
    default:
      throw new Error('This should not happen.');
  }
}

function findUnorderedListStyleType(listDepth: number): UnorderedListBulletTypesEnum {
  if (listDepth < 0) {
    throw new Error('List depth cannot be less than 0.');
  }

  const depth = listDepth % 3;

  switch (depth) {
    case 0:
      return UnorderedListBulletTypesEnum.DISC;
    case 1:
      return UnorderedListBulletTypesEnum.CIRCLE;
    case 2:
      return UnorderedListBulletTypesEnum.SQUARE;
    default:
      throw new Error('This should not happen.');
  }
}

function maybeTrimText(text: string, isFirst: boolean, isLast: boolean): string {
  if (isFirst && isLast) {
    return text.trim();
  }
  if (isFirst) {
    return text.trimStart();
  }
  if (isLast) {
    return text.trimEnd();
  }
  return text;
}

function maybeInsertAnnotations<T extends AnnotationLike>(
  node: React.ReactNode,
  currentTextContentOffset: {index: number},
  SkipTextContentIndicesComponentWrapper: React.ComponentType<{children: React.ReactNode}>,
  Annotation: AnnotationComponent<T>,
  annotations: ReadonlyArray<T>,
): ReactNode {
  if (!node) {
    return null;
  }

  if (isString(node)) {
    const rendered = renderStringWithAnnotations(node, annotations, Annotation, currentTextContentOffset);
    currentTextContentOffset.index += node.length;
    return rendered;
  }

  if (isArray(node)) {
    return Children.map(node, (child) =>
      maybeInsertAnnotations(
        child,
        currentTextContentOffset,
        SkipTextContentIndicesComponentWrapper,
        Annotation,
        annotations,
      ),
    );
  }

  // We should not encounter invalid elements here.
  // An emoji image will be a valid element with no children, and a mention would be a valid react element with a string child.
  if (!isValidElement(node)) {
    throw new Error('Invalid element encountered.');
  }

  // Nodes that should be skipped are only nodes that were transformed
  // like inline unfurled tags (and in the future @mentions).
  // On web, the domSelectionHelper will ensure these nodes and their text content cannot be partially selected.
  if (isMarkdownDecoratedSkipCountingTextContentComponent(node.type)) {
    return <SkipTextContentIndicesComponentWrapper>{node}</SkipTextContentIndicesComponentWrapper>;
  }

  // There are a few nodes that require this special handling. Like mentions (for now), and most unfurls except
  // unfurled tags.
  if (isMarkdownDecoratedCountChildrenTextContentComponent(node.type)) {
    const transformedChildren = Children.map(node.props.children, (child) =>
      maybeInsertAnnotations(
        child,
        currentTextContentOffset,
        SkipTextContentIndicesComponentWrapper,
        Annotation,
        annotations,
      ),
    );

    return cloneElement(node, node.props, transformedChildren);
  }

  throw new Error(
    'This node is not supported. Please wrap it in a decorator (see markdownDecoratedComponents.tsx) to define how it should be processed.',
  );
}
